import { createContext, useContext, useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { log } from "@/lib/logger";

const AuthContext = createContext(null);

const IDLE_LIMIT_MS = 10 * 60 * 1000;   // 10 minutes

// ============================================================
// Session: token lives in an httpOnly cookie set by the backend
// (`tiv_token`). JS cannot read it — we infer "logged in" by
// calling /auth/me on mount. No localStorage involved.
// ============================================================

// ============================================================
// SigilLockScreen — 10-minute auto-reseal overlay
// ============================================================
function SigilLockScreen({ visible, onReturn }) {
  if (!visible) return null;
  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-[#050505]/95 backdrop-blur-sm"
      data-testid="sigil-lock-screen"
    >
      <div className="absolute inset-0 grain-overlay" />
      <div className="absolute inset-0 scan-lines" />
      <div className="relative max-w-md w-full mx-6 border border-[#333] bg-[#0a0a0a] p-10 text-center space-y-7">
        <div className="text-[0.6rem] tracking-[0.4em] uppercase text-[#721C1C]">
          Sigil Closed
        </div>
        <div className="font-serif-display text-7xl leading-none">◌</div>
        <h1 className="font-serif-display text-3xl tracking-tighter">The seal has reset</h1>
        <p className="text-xs text-[#A3A3A3] leading-relaxed">
          Ten minutes have passed without your hand on the page. The vault has folded back over
          itself. Present the key again to resume the descent.
        </p>
        <button
          className="tiv-btn w-full"
          onClick={onReturn}
          data-testid="sigil-lock-return"
        >
          Re-enter the Threshold
        </button>
      </div>
    </div>
  );
}

export function AuthProvider({ children }) {
  // `null` = still hydrating · `false` = unauthenticated · object = signed-in user
  const [user, setUser] = useState(null);
  const [error, setError] = useState("");
  const [sealed, setSealed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const lastActivityRef = useRef(0);
  const idleTimerRef = useRef(null);
  const resetIdleRef = useRef(() => {}); // populated by the idle-timeout effect

  // Hydrate session on mount: ask /auth/me. The httpOnly cookie (if any)
  // travels automatically. 200 means user; anything else means unauthenticated.
  useEffect(() => {
    lastActivityRef.current = Date.now();
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/auth/me");
        if (!cancelled) setUser(res.data);
      } catch (err) {
        log.warn("[TIV] session restore failed:", err.message);
        if (!cancelled) setUser(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const login = async (username, password) => {
    setError("");
    try {
      const res = await api.post("/auth/login", { username, password });
      setUser(res.data.user);
      lastActivityRef.current = Date.now();
      setSealed(false);
      return res.data.user;
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || "Access denied.";
      setError(detail);
      return null;
    }
  };

  const intruderForce = async () => {
    setError("");
    try {
      const res = await api.post("/auth/intruder-force", {});
      setUser(res.data.user);
      lastActivityRef.current = Date.now();
      setSealed(false);
      return res.data.user;
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || "Intruder Force is sealed.";
      setError(detail);
      return null;
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout", {}); }
    catch (err) { log.warn("[TIV] logout failed:", err.message); }
    setUser(false);
  };

  // Derived role flags
  const isAuthenticated = !!user && user !== false;
  const role = isAuthenticated ? user.role : null;
  const isAdmin = role === "admin" || role === "master" || role === "intruder";
  const isMaster = role === "master";
  const isVisitor = role === "visitor";
  const isIntruder = role === "intruder";

  // Idle-timeout — only enforced for admin/master/intruder.
  // Implementation: a single setTimeout that gets RESET on every user action
  // (mousedown / keydown / touchstart / scroll / navigation). When it finally
  // fires after IDLE_LIMIT_MS of inactivity, the session is sealed. The reset
  // function is also stored in `resetIdleRef` so the navigation effect below
  // can reach it without duplicating the body.
  useEffect(() => {
    if (!isAdmin) {
      resetIdleRef.current = () => {};
      return;
    }

    const fireSeal = () => {
      api.post("/auth/logout", {}).catch(() => {});
      setUser(false);
      setSealed(true);
      toast.error("Sigil closed — session re-sealed.");
    };

    const resetTimer = () => {
      lastActivityRef.current = Date.now();
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(fireSeal, IDLE_LIMIT_MS);
    };

    resetIdleRef.current = resetTimer;
    resetTimer();
    const events = ["mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((ev) => window.addEventListener(ev, resetTimer, { passive: true }));

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, resetTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
      resetIdleRef.current = () => {};
    };
  }, [isAdmin]);

  // Treat each navigation as activity — defers to the shared resetTimer ref.
  useEffect(() => {
    if (isAdmin) resetIdleRef.current();
  }, [location.pathname, isAdmin]);

  const dismissSigil = () => {
    setSealed(false);
    navigate("/threshold");
  };

  // Memoised context value — avoids re-render churn in every consumer when
  // an unrelated parent re-renders.
  const value = useMemo(
    () => ({ user, isAuthenticated, isAdmin, isMaster, isVisitor, isIntruder, role, login, logout, intruderForce, error }),
    [user, isAuthenticated, isAdmin, isMaster, isVisitor, isIntruder, role, error]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      <SigilLockScreen visible={sealed} onReturn={dismissSigil} />
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
